import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Check, CloudUpload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";

// Optional cross-device save. Anonymous users can keep using the app fully via
// localStorage; this only offers to persist a privacy-safe derived report.
export function SaveReportCard({ nextPath = "/result" }) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { syncReport } = useApp();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await syncReport();
      if (isAuthenticated) {
        setSaved(true);
        toast.success("Laporan tersimpan di akun Anda");
      } else {
        toast.success("Laporan disiapkan. Daftar untuk menyimpannya secara permanen.");
        navigate(`/register?next=${encodeURIComponent(nextPath)}`);
      }
    } catch (e) {
      toast.error(e.message || "Gagal menyimpan laporan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="save-report-card"
      className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
          <CloudUpload className="h-4.5 w-4.5 text-emerald-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-base font-bold text-zinc-900">
            Simpan laporan ke akun
          </h3>
          <p className="mt-1 text-sm text-zinc-500">
            {isAuthenticated
              ? `Tersimpan untuk ${user?.email}. Bisa dibuka dari perangkat lain.`
              : "Simpan agar bisa dibuka dari perangkat lain. Angka keuangan Anda tetap di perangkat ini dan tidak dikirim ke server kami."}
          </p>
        </div>
      </div>

      <Button
        data-testid="save-report-button"
        onClick={handleSave}
        disabled={saving || saved}
        variant="outline"
        className="mt-4 h-11 w-full rounded-full border-zinc-300 text-sm font-semibold"
      >
        {saved ? (
          <>
            <Check className="mr-2 h-4 w-4 text-emerald-600" /> Tersimpan
          </>
        ) : (
          <>
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Menyimpan…" : isAuthenticated ? "Simpan laporan" : "Simpan & Daftar"}
          </>
        )}
      </Button>

      {!isAuthenticated && (
        <button
          data-testid="save-report-login-link"
          onClick={() => navigate(`/login?next=${encodeURIComponent(nextPath)}`)}
          className="mt-3 w-full text-center text-xs font-semibold text-zinc-500"
        >
          Sudah punya akun? Masuk
        </button>
      )}
    </div>
  );
}
